import {
  applySpec, keys, intersection, pick,
} from 'ramda';
import React, {
  createContext,
  useCallback,
  useMemo,
  memo,
} from 'react';

import {
  actions,
  selectors,
} from '@ezugi/bootstrap';
import { useAppState } from '@ezugi/bootstrap/src/hooks/useAppState';
import { useGameStatus } from '@ezugi/bootstrap/src/hooks/useGameStatus';
import { useIntl } from '@ezugi/bootstrap/src/hooks/useIntl';
import { GAME_RESULT } from '@ezugi/constants';

import {
  currentBetsSelector,
  winningBetsSelector,
} from '../store/selectors/bets';
import { showOddEvenSelector } from '../store/selectors/config';
import {
  gameResultsSelector,
  dynamicMultipliersSelector,
} from '../store/selectors/game';
import localeKeys from './localeKeys';
import { getWinningBets } from './utils';

const { payoutsSelector } = selectors;

const BetsContext = createContext();

const spec = applySpec({
  bets: currentBetsSelector,
  winningBets: winningBetsSelector,
  gameResults: gameResultsSelector,
  payouts: payoutsSelector,
  showOddEven: showOddEvenSelector,
  multipliers: dynamicMultipliersSelector,
});

const EMPTY_HIGHLIGHTS = [];

const intlSpec = { keys: localeKeys };

function BaseBetsContext(props) {
  const [
    {
      gameResults: { DiceOne, DiceTwo, DiceThree },
      payouts,
      bets,
      winningBets,
      showOddEven,
      multipliers,
    },
    dispatch,
  ] = useAppState(spec);
  const gameStatus = useGameStatus();
  const locales = useIntl(intlSpec);
  const placeBet = useCallback(
    (e) => {
      const maybeBetTarget = e.target.closest('[id*="bet-"]');

      if (maybeBetTarget) {
        dispatch(
          actions.betActions.bet.request({
            betIndex: +maybeBetTarget.id.replace('bet-', ''),
          })
        );
      }
    },
    [ dispatch ]
  );

  const highlights = useMemo(() => {
    if (typeof DiceOne === 'undefined' || gameStatus !== GAME_RESULT) return EMPTY_HIGHLIGHTS;

    return getWinningBets(DiceOne, DiceTwo, DiceThree);
  }, [ DiceOne, DiceTwo, DiceThree, gameStatus ]);

  const displayedMultipliers = useMemo(() => {
    // In GAME_RESULT we only show the winning multipliers
    if (gameStatus === GAME_RESULT) {
      return pick(
        intersection(highlights.map(Number), keys(multipliers).map(Number)),
        multipliers
      );
    }

    return multipliers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ gameStatus, winningBets, multipliers ]);

  const contextValue = useMemo(
    () => ({
      placeBet,
      locales,
      highlights,
      payouts,
      bets,
      winningBets,
      showOddEven,
      multipliers: displayedMultipliers,
    }),
    [
      placeBet,
      locales,
      highlights,
      payouts,
      bets,
      winningBets,
      showOddEven,
      displayedMultipliers,
    ]
  );

  return (
    <BetsContext.Provider value={contextValue}>
      {props.children}
    </BetsContext.Provider>
  );
}

const BetsContextProvider = memo(BaseBetsContext);

export default BetsContext;
export { BetsContextProvider };
